import Promise from "bluebird";
import Immutable from "immutable";
import unique from "lodash/uniq";
import formValueSelector from "redux-form/lib/formValueSelector";
import { createSelector } from "reselect";

import { priceAsFloat } from "../../../lib/utils";
import i18n from "../../../lib/i18n";
import { CARD_ID_RIBBON } from "../../../lib/constants";

// 14645965 Gehele dag 08:00-18:00 (standaard tarief)
// 14646028 Avond levering
// 19536998 Zondag levering
const DELIVERY_STANDARD = 14645965;
const DELIVERY_EVENING = 14646028;
const DELIVERY_SUNDAY = 19536998;
const DELIVERY_ADD_STANDARD = [DELIVERY_STANDARD, DELIVERY_SUNDAY];

const EMPTY_LIST = Immutable.List();
const EMPTY_MAP = Immutable.Map();

const valuesSelector = formValueSelector("cart");

const selectCart = createSelector(
  [
    state => valuesSelector(state, "items"),
    state => valuesSelector(state, "customer"),
    state => valuesSelector(state, "recipients"),
    state => valuesSelector(state, "checkout")
  ],
  (items, customer, recipients, checkout) =>
    Immutable.fromJS({
      items,
      customer,
      recipients,
      checkout
    })
);

const getWebProductContext = (product, productInCart, itemContexts) => {
  if (!product || !itemContexts) {
    return EMPTY_MAP;
  }

  let itemContext = itemContexts.find(item => item.get("webProductId") === product.get("webProductId"));
  if (!itemContext) {
    return EMPTY_MAP;
  }

  if (!itemContext.get("items")) {
    return itemContext;
  }

  const selectedItem = itemContext.get("items").find(i => i.get("id") === product.get("productCollectionMemberId"));
  if (!selectedItem) {
    return EMPTY_MAP;
  }
  itemContext = itemContext.set("item", selectedItem).delete("items");

  if (itemContext.get("packaging")) {
    itemContext = itemContext.set(
      "packaging",
      itemContext.get("packaging").find(item => item.get("packagingId") === productInCart.get("packagingId"))
    );
  }

  itemContext = itemContext.set(
    "extraProducts",
    itemContext.get("extraProducts").filter(p =>
      productInCart
        .get("extraProducts")
        .map(ep => ep.get("id"))
        .contains(p.get("id"))
    )
  );

  return itemContext;
};

// eslint-disable-next-line no-unused-vars
const selectAllocations = createSelector(
  [
    state => state.cart.get("context"),
    state => valuesSelector(state, "items"),
    state => valuesSelector(state, "recipients")
  ],
  (context, items, recipients) => {
    if (!context || !recipients) {
      return EMPTY_LIST;
    }

    items = Immutable.fromJS(items);
    recipients = Immutable.fromJS(recipients);

    return recipients
      .map((recipient /* , idx */) => {
        const products = recipient.get("products");
        if (!products) {
          return null;
        }
        return Immutable.Map({
          uid: recipient.get("uid"),
          title: recipient.getIn(["address", "fullName"]),
          products: products.map(product => {
            if (parseInt(product.get("quantity"), 10) === 0 || !product.get("productCollectionMemberId")) {
              return null;
            }
            // const recipientContext = context.getIn(['recipientContexts', idx], EMPTY_MAP);

            const productInCart = items.find(item => item.get("uid") === product.get("uid"));

            const webProduct = getWebProductContext(product, productInCart, context.get("itemContexts"));

            let list = Immutable.List();

            list = list.push(
              new Immutable.Map({
                uid: product.get("uid"),
                title: [webProduct.getIn(["title"]), webProduct.getIn(["item", "title"])].filter(v => !!v).join(" "),
                quantity: product.get("quantity"),
                price: product.get("quantity") * webProduct.getIn(["item", "priceEx"])
              })
            );

            // webProduct.item.priceEx
            if (productInCart.get("vase") && webProduct.get("canHaveVase")) {
              list = list.push(
                new Immutable.Map({
                  uid: `vase${webProduct.getIn(["item", "vasePriceEx"])}`,
                  title: `${i18n.t("vase")} ${webProduct.getIn(["item", "title"])}`,
                  quantity: product.get("quantity"),
                  price: webProduct.getIn(["item", "vasePriceEx"]) * product.get("quantity")
                })
              );
            }

            // related products
            const relatedProduct = webProduct.get("relatedProduct");
            if (webProduct.get("hasRelatedProduct") && productInCart.get("extraProduct") && relatedProduct) {
              list = list.push(
                new Immutable.Map({
                  uid: relatedProduct.get("id"),
                  title: relatedProduct.get("title"),
                  quantity: product.get("quantity"),
                  price: priceAsFloat(relatedProduct.get("priceEx") * product.get("quantity"))
                })
              );
            }
            return list;
          })
        });
      })
      .filter(v => !!v);
  }
);

const getUniqueDeliveryAddresses = (cart, context) => {
  const hashes = [];
  const recipients = [];
  const recipientContexts = [];

  cart.get("recipients").forEach((recipient, idx) => {
    const recipientContext = context.getIn(["recipientContexts", idx]);
    if (!recipientContext) {
      return;
    }
    const availableTimeFrames = recipientContext.get("availableTimeFrames");

    if (!availableTimeFrames) {
      return;
    }

    const timeFrames = availableTimeFrames
      .filter(timeFrame => {
        const id = timeFrame.get("id");
        if (id === 14645965) {
          // always add standard (else it's price will be ignored)
          return true;
        }
        // now add any other (morning, afternoon, night etc)
        return id === recipient.getIn(["delivery", "timeFrame"], 0);
      })
      .map(timeFrame => timeFrame.get("id"));

    const key = JSON.stringify(
      [
        recipient.getIn(["address", "countryCode"], 0),
        recipient.getIn(["address", "zipcode"], 0),
        recipient.getIn(["address", "streetNumber"], 0),
        recipient.getIn(["delivery", "date"], idx),
        timeFrames.join(",")
      ].join(",")
    );

    if (hashes.indexOf(key) === -1) {
      hashes.push(key);
      recipients.push(recipient);
      recipientContexts.push(recipientContext);
    }
  });

  return Promise.all([recipients, recipientContexts]);
};

const getRecipientShippingCosts = (recipient, recipientContext) => {
  if (!recipientContext) {
    return 0;
  }
  const availableTimeFrames = recipientContext.get("availableTimeFrames", null);
  if (!availableTimeFrames) {
    return [];
  }
  const availableDates = recipientContext.get("availableDates", null);
  if (!availableDates || !availableDates.get("shippingPrice")) {
    return [];
  }

  const timeFrameId = recipient.getIn(["delivery", "timeFrame"], 0);
  if (!timeFrameId) {
    return [];
  }

  const thereIsOnlyOne = availableTimeFrames.size === 1;

  if (thereIsOnlyOne) {
    const first = availableTimeFrames.get(0);
    if (first.get("id") === DELIVERY_EVENING) {
      return availableTimeFrames.set(
        0,
        first.set("totalPrice", first.get("priceEx", 0) + availableDates.get("shippingPrice", 0))
      );
    }
  }

  return availableTimeFrames
    .filter(timeFrame => {
      if (DELIVERY_ADD_STANDARD.indexOf(timeFrame.get("id")) !== -1) {
        // always add standard/evening/sunday (else it's price will be ignored)
        return true;
      }
      // now add any other (morning, afternoon, night etc)
      return timeFrame.get("id") === timeFrameId;
    })
    .map(t => {
      if (DELIVERY_ADD_STANDARD.indexOf(t.get("id")) !== -1) {
        // standard/evening/sunday so requires the shippingPriceEx + the product price
        // always add standard/evening/sunday (else it's price will be ignored)
        return t.set("totalPrice", t.get("priceEx", 0) + availableDates.get("shippingPrice", 0));
      }
      return t.set("totalPrice", t.get("priceEx", 0));
    });
};

const getShippingCosts = (cart, context) => {
  let pricesEx = EMPTY_LIST;
  return getUniqueDeliveryAddresses(cart, context).spread((recipients, recipientContexts) => {
    recipients.forEach((recipient, idx) => {
      pricesEx = pricesEx.concat(getRecipientShippingCosts(recipient, recipientContexts[idx]));
    });
    return pricesEx;
  });
};

const getRecipientOtherCosts = (recipient, recipientContext) => {
  if (!recipientContext) {
    return 0;
  }
  const availableCards = recipientContext.get("availableCards");
  if (!availableCards) {
    return 0;
  }

  return availableCards
    .filter(
      card => card.get("id") === recipient.getIn(["card", "type"], 0)
    )
    .map(card => card.get("priceEx", 0)) // just the price is right
    .reduce((total, priceEx) => {
      total += priceEx;
      return total;
    }, 0); // lets add it all up;
};

/**
 * type of other costs
 *
 *  1 is only ribbons
 *  0 is both ribbon/card
 * -1 is only cards
 *
 * @param recipientContexts
 *
 * @returns {*}
 */
const getOverviewOtherType = (items, recipients) => {
  if (!items || items.size === 0 || !recipients || recipients.size === 0) {
    return 0;
  }
  return unique(recipients.map(ctx => ctx.getIn(["card", "type"], 0)).toJS()).reduce((total, cardId) => {
    if (cardId === CARD_ID_RIBBON) {
      total += 1;
    } else {
      total -= 1;
    }
    return total;
  }, 0);
};

const hasCard = recipient =>
  recipient.getIn(["card", "type"]) &&
  (recipient.getIn(["card", "text", "left"], recipient.getIn(["card", "text"], null)) ||
    recipient.getIn(["address", "street"]));

/**
 * returns excl tax costs
 *
 * @param cart
 * @param context
 * @returns {number}
 */
const getTotalOtherCosts = (cart, context) => {
  if (cart.get("items").size === 0) {
    return 0;
  }
  let priceEx = 0;

  cart.get("recipients").forEach((recipient, idx) => {
    const recipientContext = context.getIn(["recipientContexts", idx]);
    if (!recipientContext || !hasCard(recipient)) {
      return;
    }
    priceEx += getRecipientOtherCosts(recipient, recipientContext);
  });
  return priceEx;
};

/**
 * returns excl tax costs
 *
 * @param cart
 * @param context
 * @returns {*}
 */
const getTotalPaymentCosts = (cart, context) => {
  const paymentMethodId = cart.getIn(["checkout", "payment", "type"], 0);
  if (!paymentMethodId) {
    return 0;
  }
  // could have one or more payment methods, i.e. an extra surcharge or something
  return context
    .get("paymentMethods")
    .filter(p => p.get("id") === (paymentMethodId || 0))
    .map(p => p.get("priceEx", 0)) // just the price is right
    .reduce((total, priceEx) => {
      total += priceEx;
      return total;
    }, 0); // lets add it all up ;
};

const getExtraProductPriceEx = product =>
  (product.get("priceEx") * product.get("taxMultiplier") - product.get("discount")) / product.get("taxMultiplier");

/**
 * returns product costs
 *
 * @param cart
 * @param context
 * @returns {*}
 */
const getProductCosts = (cart, context) => {
  let priceEx = 0;

  const items = cart.get("items");
  const itemContexts = context.get("itemContexts");

  cart.get("recipients").forEach(recipient => {
    recipient.get("products").forEach(product => {
      if (parseInt(product.get("quantity"), 10) === 0) {
        return;
      }

      const productInCart = items.find(i => i.get("uuid") === product.get("uuid"));
      if (!productInCart) {
        return;
      }

      const webProduct = getWebProductContext(product, productInCart, itemContexts);

      const title = webProduct.getIn(["item", "title"], null);
      if (title) {
        priceEx += priceAsFloat(webProduct.getIn(["item", "priceEx"], 0) * product.get("quantity"));
      }

      if (productInCart.get("extraProducts") && webProduct.get("extraProducts")) {
        priceEx += webProduct.get("extraProducts").reduce((totalPriceEx, p) => {
          if (p.get("id") === -123) {
            p = p.set("priceEx", webProduct.getIn(["item", "vasePriceEx"], 0));
          }
          totalPriceEx += getExtraProductPriceEx(p) * product.get("quantity");
          return totalPriceEx;
        }, 0);
      }
    });
  });
  return priceEx;
};

/**
 * @param cart
 * @param context
 * @param shippingCosts
 * @param other
 * @param payment
 * @returns {{price: number, highestTaxMultiplier: number}}
 */
const getTotalTaxCosts = (cart, context, shippingCosts, other, payment) => {
  const defaultTaxMultiplier = priceAsFloat(context.get("defaultTaxMultiplier") - 1);

  let highestTaxMultiplier = 0;

  let tax = 0;

  // 1: product is own tax
  cart.get("recipients").forEach((recipient /* , idx */) => {
    recipient.get("products").forEach(product => {
      if (parseInt(product.get("quantity"), 10) === 0) {
        return;
      }

      const productInCart = cart.get("items").find(i => i.get("uuid") === product.get("uuid"));
      if (!productInCart) {
        return;
      }
      const webProduct = getWebProductContext(product, productInCart, context.get("itemContexts"));

      // 1a: product is own vat
      const priceEx = webProduct.getIn(["item", "priceEx"], 0);
      if (priceEx) {
        const taxMultiplier = priceAsFloat(webProduct.getIn(["item", "taxMultiplier"], 0) - 1);

        let rawPrice = product.get("quantity") * priceEx;

        rawPrice *= taxMultiplier;

        tax += priceAsFloat(rawPrice);

        highestTaxMultiplier = Math.max(highestTaxMultiplier, taxMultiplier);
      }

      // 1b: vase is inc default vat
      const vasePriceEx = webProduct.getIn(["item", "vasePriceEx"], 0);

      // 1c: packaging is inc default vat
      const packagingPriceEx = webProduct.getIn(["packaging", "priceEx"], 0);
      if (productInCart.get("packagingId") && packagingPriceEx) {
        tax += priceAsFloat(packagingPriceEx * product.get("quantity") * defaultTaxMultiplier);
      }

      //
      const extraProducts = productInCart.get("extraProducts");
      if (extraProducts && extraProducts.size) {
        webProduct.get("extraProducts").forEach(extraProduct => {
          const thisTaxMultiplier = priceAsFloat(extraProduct.get("taxMultiplier") - 1);
          highestTaxMultiplier = Math.max(highestTaxMultiplier, thisTaxMultiplier);
          if (extraProduct.get("id") === -123) {
            tax += priceAsFloat(vasePriceEx * product.get("quantity") * defaultTaxMultiplier);
            return;
          }
          tax += priceAsFloat(extraProduct.get("priceEx") * product.get("quantity") * thisTaxMultiplier);
        });
      }
    });
  });

  // 4: total other is excl default tax
  if (other > 0) {
    tax += priceAsFloat(other * defaultTaxMultiplier);
  }

  // 2a: shipping costs is excl highest applicable vat
  // 2b: shipping toeslag is excl default tax
  if (shippingCosts.size > 0) {
    const shippingTaxes = priceAsFloat(
      shippingCosts
        .map(timeFrame => {
          const taxMultiplier = priceAsFloat(timeFrame.get("taxMultiplier") - 1);
          return timeFrame.get("totalPrice") * taxMultiplier;
        })
        .reduce((total, t) => total + t, 0)
    ); // lets add it all up ;

    tax += shippingTaxes;
  }

  // i.e. trikerbike
  if (highestTaxMultiplier === 0) {
    highestTaxMultiplier = defaultTaxMultiplier;
  }

  // 3: payment costs is inc. default vat
  if (payment > 0) {
    tax += priceAsFloat(payment * defaultTaxMultiplier);
  }

  return {
    price: tax,
    highestTaxMultiplier
  };
};

const getDiscounts = context => context.getIn(["discounts", "discounts"], []);

// 'total' => $totalPrice + $shippingCosts + $otherCosts + $paymentCosts + $tax + $discounts,
const getCartTotals = (cart, context) =>
  Promise.all([
    getProductCosts(cart, context),
    getShippingCosts(cart, context),
    getTotalOtherCosts(cart, context),
    getTotalPaymentCosts(cart, context)
  ])
    .spread((products, shippingCosts, other, payment) =>
      Promise.all([
        products,
        shippingCosts,
        other,
        payment,
        getTotalTaxCosts(cart, context, shippingCosts, other, payment),
        getDiscounts(context)
      ])
    )
    .spread((products, shipping, other, payment, tax, discounts) => {
      shipping = shipping
        .map(timeFrame => timeFrame.get("totalPrice"))
        .reduce((total, price) => {
          // lets add it all up
          total += price;
          return total;
        }, 0);

      discounts = discounts.map(discount =>
        discount
          .set("taxMultiplier", tax.highestTaxMultiplier)
          .set("priceEx", discount.get("value"))
          .set("value", discount.get("value") * (1 + tax.highestTaxMultiplier))
      );

      // lets add it all up
      const totalDiscount = discounts.reduce((total, discount) => total + discount.get("value"), 0);

      return Immutable.OrderedMap({
        productCosts: priceAsFloat(products),
        shippingCosts: priceAsFloat(shipping),
        paymentCosts: priceAsFloat(payment),
        [`otherCosts${getOverviewOtherType(cart.get("items"), cart.get("recipients"))}`]: priceAsFloat(other),
        totalEx: priceAsFloat(products) + priceAsFloat(shipping) + priceAsFloat(other) + priceAsFloat(payment),
        tax: priceAsFloat(tax.price),
        discounts: discounts.toJS(),
        totalIn:
          priceAsFloat(products) +
          priceAsFloat(shipping) +
          priceAsFloat(other) +
          priceAsFloat(payment) +
          priceAsFloat(tax.price) +
          priceAsFloat(totalDiscount)
      });
    });

export const getTemplateData = state => {
  const cart = selectCart(state);
  const context = state.cart.get("context");
  const itemContexts = context && context.get("itemContexts");
  if (!cart || !context || !itemContexts || itemContexts.size === 0) {
    return Promise.resolve(EMPTY_MAP);
  }
  return getCartTotals(cart, context).then(totals => {
    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.log("totals", totals.toJS());
    }
    return totals;
  });
};

export default getTemplateData;
